import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { InlineNotification, Loading } from '@carbon/react';
import routes from '~/routes';
import { createResourceAtom } from 'lib/atoms';
import { createMutationAtom } from 'atoms/create-resource-atom';
import { formatResource } from 'utils/misc';
import { DualSidebarLayout } from 'components/layout/dual-sidebar-layout';
import { TypeCurveDetailsContent } from 'pages/type-curves/type-curve-details-content';
import { useUnitSpacingConfigs } from 'pages/type-curves/use-unit-spacing-configs';
import { useInventoryGunBarrelFor } from 'pages/type-curves/inventory-surface/use-inventory-gun-barrel-for';
import { useUnitFormationSlotsFor } from 'pages/type-curves/inventory-surface/use-unit-formation-slots-for';
import {
  InventoryDetailsSection,
  InventorySpacingValues,
} from 'pages/type-curves/inventory-details-section';
import { TypeCurvePageFilters } from 'pages/type-curves/type-curve-filters/type-curve-page-filters';
import { TcWellFilterModal } from 'pages/type-curves/type-curve-filters/tc-well-filter-modal';
import { TcUnitFilterModal } from 'pages/type-curves/type-curve-filters/tc-unit-filter-modal';
import { TcColorByModal } from 'pages/type-curves/type-curve-filters/tc-color-by-modal';
import { TcUnitColorByModal } from 'pages/type-curves/type-curve-filters/tc-unit-color-by-modal';
import { TcCrossPlotChartModal } from 'pages/type-curves/type-curve-filters/tc-cross-plot-chart-modal';
import { DcaScenarioSelection } from 'pages/type-curves/dca-scenario-selection';
import { selectedDcaScenarioAtom } from 'components/well-page-content/atoms';
import {
  INVENTORY_PAGE_SECTIONS,
  type TcWellFilters,
  type ColorByConfig,
} from 'pages/type-curves/types';
import {
  toastAtom,
  ltdRefreshTriggerAtom,
  geometryRefreshAtom,
  tcInventoryHighlightedAreaAtom,
} from 'pages/type-curves/atoms';
import { subscribe } from 'lib/pusher';
import { useUnitRegenerationProgress } from './unit-builder/use-unit-regeneration-progress';
import { UnitRegenerationBanner } from './unit-builder/unit-regeneration-banner';
import {
  tcWellsTotalCountAtom,
  tcUnitsTotalCountAtom,
  tcHasTypeCurveAreaAtom,
  tcSetbackZoneIdAtom,
  tcHighlightedFormationAtom,
  tcInventoryWellScopeAtom,
  tcInventoryGunBarrelScopeAtom,
  tcInventorySpacingAtom,
  tcWellFiltersAtom,
  tcInventoryAreaFormationsAtom,
  tcColorByRulesAtom,
  tcSelectedDevAreaAtom,
} from 'pages/type-curves/type-curve-filters/atoms';
import { useInitSelectedDcaScenario } from './use-init-dca-scenario';
import { useInitInventoryViewDefaults } from './use-init-inventory-view-defaults';
import {
  detailsFormationName,
  treeFormationName,
  type InventorySlotRequestResponse,
  type InventoryTypeCurveDetailsResponse,
  type InventoryWorkspaceResponse,
  type TypeCurveHeader,
} from 'types/api-responses';
import { formationSwatchColor } from './formation-swatch-colors';
import {
  applyTypeCurvePicks,
  applyPackagePicks,
  filterTreeByZones,
  mergeContextZones,
  selectTypeCurvePerFormation,
  resolveTreeSelections,
  resolveTreeLeaves,
  resolveTreeZoneKeys,
  resolveTreePackages,
  treeZoneKey,
  defaultTreePackageIds,
  type InventoryTreeLeafRef,
  type TreeTypeCurvePick,
  type TreeZonePackage,
  type UpdatedContextRow,
} from './tree-leaves';
import { useTreePackagePicks } from './hooks';
import { CreateInventoryWorkspaceModal } from 'components/type-curve-workspace/create-inventory-workspace-modal';
import { InventoryWorkspaceHeader } from './inventory-workspace-header';
import { EventsPanel } from 'components/events-panel';
import { InventoryTypeCurveTree } from './type-curve-tree';
import { TcUnitNavigator } from 'pages/type-curves/tc-unit-navigator';
import {
  seedBuilderFromMainAtom,
  resetBuilderSurfaceAtom,
  spacingEditsAtoms,
  builderSectionCrossAtom,
} from 'pages/type-curves/inventory-surface/atoms';
import { PromoteWorkspaceModal } from './promote-workspace-modal';
import {
  UnitBuilderModal,
  UnitBuilderMap,
  UnitBuilderGunBarrel,
  builderOpenAtom,
  builderScopedUnitAtom,
  surveyRowsAtom,
} from './unit-builder';
import { WorkspaceTasksPanel } from './workspace-tasks-panel';
import { InventoryVerificationPanel } from './inventory-verification-panel';
import {
  useInventoryWorkspaceTasks,
  useSaveInventoryWorkspaceTask,
} from './use-inventory-workspace-tasks';
import { useFoucsExclusionsGate } from './use-foucs-exclusions-gate';
import { inventoryRightPanelAtom } from './atoms';

// The workspace-level gate task, kept out of the checklist and driven by the
// header's Mark as Correct / Incorrect control instead.
const GATE_TASK_PATH = 'inventory_workspace';

// Pusher event SlotInventoryRequest fires on every status transition. The bulk
// Compute Inventory run reports completion over this event, not on the 202.
const SLOT_STATUS_EVENT = 'slot_inventory_request.status.changed';
const SLOT_TERMINAL_STATUSES = new Set(['processed', 'failed', 'cancelled']);

import style from 'pages/type-curves/type-curves-details.module.scss';

// Compute Inventory — one slot-generation run for the selected type curve.
const computeInventoryAtom = createMutationAtom<
  InventorySlotRequestResponse,
  'createInventorySlotRequest'
>();

// Owns the BUILDER surface's gun-barrel + slot-table fetches. Rendered only while
// the Unit Builder is open (so the hooks mount/unmount with it), and fed the SAME
// header id / collection id the main-surface hooks use, so the builder's request
// signature matches the main's — letting the deep-copy seed be reused instead of
// re-fetched when the main page already has the data.
const BuilderSurfaceFetchers = ({
  typeCurveId,
  structureGridCollectionId,
}: {
  typeCurveId: number | null;
  structureGridCollectionId: number | null;
}) => {
  useInventoryGunBarrelFor('builder', typeCurveId, structureGridCollectionId);
  useUnitFormationSlotsFor('builder', typeCurveId, structureGridCollectionId);
  return null;
};

// Inventory Workspace page (INS-10547). Same body as the Type Curve details
// page — the workspace wraps a Type Curve's details content (summary, map,
// inventory management, wells, units) under its own lifecycle header. The show
// payload carries the typeCurveTree left-nav; selecting a leaf drives the
// leaf's inventory details (spacing/setbacks/property counts) and its header
// (fetched by tc_public_id — the leaf's label), which then powers the ordinary
// Type Curve sections.
export const InventoryWorkspacePage = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [leftOpen, setLeftOpen] = useState(true);
  const [editingColorRuleId, setEditingColorRuleId] = useState<string | null>(
    null
  );
  const [editingUnitColorRuleId, setEditingUnitColorRuleId] = useState<
    string | null
  >(null);
  const [selectedLeaf, setSelectedLeaf] = useState<InventoryTreeLeafRef | null>(
    null
  );
  const [promoteModalOpen, setPromoteModalOpen] = useState(false);
  const [editCurvesOpen, setEditCurvesOpen] = useState(false);
  /**
   * Zone ids the left-nav shows, or null for whatever the response carried.
   *
   * Frontend-only: no endpoint edits a workspace's context, so add/remove
   * narrows the rendered tree and a reload returns to the server's own set.
   */
  const [includedZones, setIncludedZones] =
    useState<ReadonlySet<string> | null>(null);
  /**
   * The picker's full resolved context per included zone, keyed by zone id.
   * Frontend-only, like the zone selection: no endpoint accepts a chosen curve
   * or package, so a reload returns to the server's own set. Both the per-zone
   * type-curve and package picks are DERIVED from this, and the zones the
   * workspace wasn't created with are grown from it (`mergeContextZones`).
   */
  const [contextRows, setContextRows] = useState<
    Record<string, UpdatedContextRow>
  >({});
  // Type curve chosen per zone, off the reported context. Absent means the zone
  // is on the tree's own curve.
  const typeCurvePicks = useMemo<Record<string, TreeTypeCurvePick>>(
    () =>
      Object.fromEntries(
        Object.values(contextRows).map((row) => [row.rowId, row.typeCurve])
      ),
    [contextRows]
  );
  // Packages picked from the tree's own package nodes, applied over the
  // picker's: a tree click is the later choice.
  const {
    picks: treePackagePicks,
    selectPackage,
    retain: retainTreePackagePicks,
  } = useTreePackagePicks(setSelectedLeaf);
  // Inventory package chosen per zone, by `inventoryFormationPackageId` —
  // `applyPackagePicks` promotes it so the leaf resolves the picked package's
  // detail id. Rows without an id (add candidates the workspace doesn't own)
  // can't re-key a leaf, so they are left out.
  const packagePicks = useMemo<Record<string, number>>(() => {
    const entries: [string, number][] = [];
    for (const row of Object.values(contextRows)) {
      const packageId = row.package?.inventoryFormationPackageId;
      if (packageId !== null && packageId !== undefined) {
        entries.push([row.rowId, packageId]);
      }
    }
    return { ...Object.fromEntries(entries), ...treePackagePicks };
  }, [contextRows, treePackagePicks]);
  // The reported rows as a list, for growing the tree with checked zones the
  // workspace wasn't created with.
  const contextRowList = useMemo(
    () => Object.values(contextRows),
    [contextRows]
  );
  const [builderOpen, setBuilderOpen] = useAtom(builderOpenAtom);
  // The scoped unit is derived from the shared map selection, so paging the
  // builder header re-scopes everything (boundary, slots, setback, camera).
  const scopedUnit = useAtomValue(builderScopedUnitAtom);
  const setSurveyRows = useSetAtom(surveyRowsAtom);
  // Deep-copy the main surface's gun barrel + slot table into the builder on open
  // (when main already holds the unit's data), and clear the builder's copies on
  // close so the next open re-seeds fresh.
  const seedBuilderFromMain = useSetAtom(seedBuilderFromMainAtom);
  const resetBuilderSurface = useSetAtom(resetBuilderSurfaceAtom);
  const [rightPanel, setRightPanel] = useAtom(inventoryRightPanelAtom);
  const wellsTotalCount = useAtomValue(tcWellsTotalCountAtom);
  const unitsTotalCount = useAtomValue(tcUnitsTotalCountAtom);
  const selectedDcaScenario = useAtomValue(selectedDcaScenarioAtom);
  const setHasTypeCurveArea = useSetAtom(tcHasTypeCurveAreaAtom);

  // Auto-select a default DCA scenario on load, so the sidebar selector appears
  // and the right-panel picker is reachable.
  useInitSelectedDcaScenario();

  // DCA Parameters + Summary Table sections start hidden; the Charts section
  // stays visible but every chart starts in its "Hide All" state.
  useInitInventoryViewDefaults();

  // keep-old-data: the rollup refetches after every verdict, and clearing the
  // payload would flash the whole page through Loading on each one.
  const showAtom = useMemo(
    () =>
      createResourceAtom<InventoryWorkspaceResponse>(
        id ? `/inventory_workspaces/${id}` : undefined,
        'keep-old-data-on-refetch'
      ),
    [id]
  );
  const [showResource, refreshShow] = useAtom(showAtom);
  const { data: workspace, error: showError } = formatResource(showResource);

  // After the workspace GET resolves, force a FOUCS exclusions recompute and gate
  // the map's development-areas + slot-geometries layers on its 200 — those layers
  // render units/slots whose scope the recompute decides.
  useFoucsExclusionsGate(id, !!workspace);

  // Fetch the scoped unit's per-formation spacing configs ONCE here (the page is
  // mounted for the whole workspace, unlike the gun barrel which the Unit Builder
  // remounts) and publish to `tcUnitSpacingConfigsAtom`. Both the main-page and
  // Unit Builder gun barrels read that atom; the hook re-fetches on LTD generation.
  useUnitSpacingConfigs();

  // Verification checklist (workspace-level). One fetch, shared by the header's
  // gate control and the tasks panel. The gate task is split out and driven from
  // the header; the panel and the "Tasks X/Y" badge cover the rest.
  const workspaceIdNum = id ? Number(id) : undefined;
  const tasksQuery = useInventoryWorkspaceTasks(workspaceIdNum);
  const saveGateTask = useSaveInventoryWorkspaceTask(workspaceIdNum);
  const allTasks = useMemo(
    () => tasksQuery.data?.tasks ?? [],
    [tasksQuery.data]
  );
  const gateTask = useMemo(
    () => allTasks.find((task) => task.path === GATE_TASK_PATH) ?? null,
    [allTasks]
  );
  const checklistTasks = useMemo(
    () => allTasks.filter((task) => task.path !== GATE_TASK_PATH),
    [allTasks]
  );
  // Recompute over the checklist only — the server counts include the gate.
  const checklistCompleted = checklistTasks.filter(
    (task) => task.status !== null
  ).length;

  // Promote readiness, mirroring the Type Curve workspace: every REQUIRED
  // checklist task carries a verdict AND the workspace gate has been marked
  // correct/incorrect, on an unpromoted workspace. False before tasks load
  // (empty list), so Promote stays disabled until verification is known.
  const requiredChecklistTasks = checklistTasks.filter((task) => task.required);
  const canPromote =
    !!workspace &&
    !workspace.promoted &&
    requiredChecklistTasks.length > 0 &&
    requiredChecklistTasks.every((task) => task.status !== null) &&
    (gateTask?.status ?? null) !== null;

  // The tree the page works from: the response narrowed by add/remove. Every
  // consumer reads this, so a dropped zone leaves the left-nav, the formation
  // stepper and the default selection together.
  const visibleTree = useMemo(() => {
    if (!workspace) return null;
    // Grow the tree with any checked zones the workspace wasn't created with,
    // narrow to the included set, then reduce each formation to its single
    // selected type curve before applying the operator's curve/package picks —
    // so a formation reads as one row and a pick still wins.
    return applyTypeCurvePicks(
      applyPackagePicks(
        selectTypeCurvePerFormation(
          filterTreeByZones(
            mergeContextZones(workspace.typeCurveTree, contextRowList),
            includedZones
          )
        ),
        packagePicks
      ),
      typeCurvePicks
    );
  }, [workspace, includedZones, typeCurvePicks, packagePicks, contextRowList]);

  // Default selection: the launched (origin) type curve, once per workspace
  // arrival. No-ops after a user pick so keep-old-data refetches don't clobber.
  useEffect(() => {
    if (selectedLeaf || !workspace || !visibleTree) return;
    const leaves = resolveTreeLeaves(visibleTree);
    setSelectedLeaf(
      leaves.find((leaf) => leaf.uniqueTcId === workspace.uniqueTcId) ??
        leaves[0] ??
        null
    );
  }, [workspace, visibleTree, selectedLeaf]);

  // The selected leaf's inventory details (spacing, setbacks, property counts).
  const inventoryTypeCurveAtom = useMemo(
    () =>
      createResourceAtom<InventoryTypeCurveDetailsResponse>(
        id && selectedLeaf?.inventoryFormationPackageId
          ? `/inventory_workspaces/${id}/inventory_formation_packages/${selectedLeaf.inventoryFormationPackageId}`
          : undefined,
        'keep-old-data-on-refetch'
      ),
    [id, selectedLeaf?.inventoryFormationPackageId]
  );
  const [inventoryTypeCurveResource, refreshInventoryTypeCurve] = useAtom(
    inventoryTypeCurveAtom
  );

  // BOTH builder actions re-resolve the zone's inventory details — Update
  // Geometries persists a slot move/trim, and Generate LTD re-lays the unit's
  // slots outright — so the inventory-package resource refreshes on
  // `geometryRefreshAtom`, the signal each of them drives. Skip the mount value so
  // the resource isn't double-fetched on first render (it already loads on mount
  // and on leaf change).
  const geometryRefresh = useAtomValue(geometryRefreshAtom);
  const prevGeometryRefresh = useRef(geometryRefresh);
  useEffect(() => {
    if (prevGeometryRefresh.current === geometryRefresh) return;
    prevGeometryRefresh.current = geometryRefresh;
    void refreshInventoryTypeCurve();
  }, [geometryRefresh, refreshInventoryTypeCurve]);

  // The selected leaf's zone drives the map's setback outline. The tile draws it
  // only when told which zone measures it, and clearing on unmount keeps the
  // outline off a subsequently-viewed plain Type Curve page.
  const setSetbackZoneId = useSetAtom(tcSetbackZoneIdAtom);
  useEffect(() => {
    setSetbackZoneId(selectedLeaf?.inventoryFormationPackageId ?? null);
    return () => setSetbackZoneId(null);
  }, [selectedLeaf?.inventoryFormationPackageId, setSetbackZoneId]);
  const { data: inventoryTypeCurve } = formatResource(
    inventoryTypeCurveResource
  );

  // ─── Selected zone's units → Wells section spatial bound ───
  // The zone's dissolved unit footprint replaces the Type Curve Area polygon in
  // the wells query, matched against the perforated lateral, so the Wells count
  // and Property Counts by Status resolve the same well set. Cleared while the
  // zone details are in flight and on unmount, so a plain Type Curve page keeps
  // the area-polygon bound.
  const setInventoryWellScope = useSetAtom(tcInventoryWellScopeAtom);
  const unitScopeGeojson = inventoryTypeCurve?.unitScopeGeojson;
  useEffect(() => {
    setInventoryWellScope(
      unitScopeGeojson?.coordinates?.length ? { unitScopeGeojson } : null
    );
    return () => setInventoryWellScope(null);
  }, [unitScopeGeojson, setInventoryWellScope]);

  const [{ loading: computingInventory }, computeInventory] =
    useAtom(computeInventoryAtom);
  const setToast = useSetAtom(toastAtom);
  // Bumping this re-runs the map slot-geometries layer, the slot table and the
  // gun barrel — the three surfaces that render generated LTD slots.
  const setLtdRefreshTrigger = useSetAtom(ltdRefreshTriggerAtom);

  // A rotate (from the Unit Builder) re-lays every formation package of the unit
  // asynchronously. This holds the ONE subscription to that unit's regeneration
  // channel and advances the shared tally the banner reads — here and in the
  // modal — refreshing the slot surfaces as each formation lands.
  useUnitRegenerationProgress();

  // The bulk Compute run is async (202 + a Pusher channel), so the button stays
  // in its computing state until the run reports terminal over Pusher. The
  // channel is tracked for cleanup on unmount.
  const [computeRunInProgress, setComputeRunInProgress] = useState(false);
  // A compute error response (validation, e.g. the formation can't be computed)
  // keeps Compute Inventory disabled until the operator changes formation.
  const [computeBlocked, setComputeBlocked] = useState(false);
  const computeChannelRef = useRef<ReturnType<typeof subscribe> | null>(null);
  useEffect(
    () => () => {
      computeChannelRef.current?.unbind(SLOT_STATUS_EVENT);
      computeChannelRef.current?.unsubscribe();
      computeChannelRef.current = null;
    },
    []
  );

  // Compute Inventory: kick a bulk slot-generation run for the workspace. The
  // edited spacing values ride on the request; the run is async, so we subscribe
  // to its Pusher channel and, on the terminal `processed` event, refresh the
  // node details AND the map slots / gun barrel / slot table (via the trigger).
  // Resolves whether the run succeeded, so a rejected request (NO_AREA_CONFIG,
  // an unreachable server) leaves the section's edits dirty and its button
  // enabled instead of stranding the user on a disabled Compute.
  const handleComputeInventory = (spacing: InventorySpacingValues) =>
    new Promise<boolean>((resolve) => {
      if (!id || !selectedLeaf?.inventoryFormationPackageId) {
        resolve(false);
        return;
      }

      // Refresh the surfaces that show generated slots — the node details plus
      // the map slots / gun barrel / slot table (via the shared trigger).
      const refreshSlots = () => {
        // Refetch the zone details (spacing + `hasComputedInventory`), which
        // re-disables the Compute button until the next spacing edit, plus the
        // map slots / gun barrel / slot table via the shared trigger.
        void refreshInventoryTypeCurve();
        setLtdRefreshTrigger((prev) => prev + 1);
      };

      setComputeRunInProgress(true);
      computeInventory({
        url: `/inventory_workspaces/${id}/slot_inventory_requests`,
        type: 'POST',
        data: {
          generation: {
            inventoryFormationPackageId:
              selectedLeaf.inventoryFormationPackageId,
            force: true,
            wellSpacingFt: spacing.wellSpacingFt,
            crossLineSetbackFt: spacing.crossLineSetbackFt,
            inLineSetbackFt: spacing.inLineSetbackFt,
            spacingToleranceFt: spacing.spacingToleranceFt,
          },
        },
        onSuccess: (data) => {
          const channelName = data?.notifications?.channel;
          // No channel means the run is already done (nothing eligible, or an
          // instant completion) — refresh immediately.
          if (!channelName) {
            refreshSlots();
            setComputeRunInProgress(false);
            resolve(true);
            return;
          }

          // Async run: wait for the terminal status over Pusher, then refresh.
          const channel = subscribe(channelName);
          computeChannelRef.current = channel;
          channel.bind(SLOT_STATUS_EVENT, (evt: { status?: string }) => {
            if (!evt.status || !SLOT_TERMINAL_STATUSES.has(evt.status)) return;
            channel.unbind(SLOT_STATUS_EVENT);
            channel.unsubscribe();
            computeChannelRef.current = null;
            setComputeRunInProgress(false);

            if (evt.status === 'processed') {
              refreshSlots();
              setToast({
                kind: 'success',
                title: 'Inventory computed successfully',
              });
              resolve(true);
            } else {
              setToast({
                kind: 'error',
                title: 'Inventory computation failed',
              });
              resolve(false);
            }
          });
        },
        // `undefined` on a fatal (transport) failure; otherwise the API's own
        // errors[] — its first message is the actionable one.
        onError: (err) => {
          setComputeRunInProgress(false);
          setToast({
            kind: 'error',
            title: 'Unable to compute inventory',
            subtitle:
              err?.errors?.[0]?.message ?? 'The run could not be started.',
          });
          // A validation error response (e.g. the formation can't be computed)
          // blocks Compute until the operator changes formation. A fatal /
          // transport failure (`err` undefined) stays retryable.
          if (err) setComputeBlocked(true);
          resolve(false);
        },
      });
    });

  // Flat leaf order, matching the tree's own render order — what the builder's
  // formation stepper walks. Shallower is up, deeper is down, so the arrows just
  // move through this list.
  /**
   * Apply an add/remove selection to the rendered tree.
   *
   * The picker reports the full resolved context (chosen type curve + inventory
   * package) of every checked row, plus the complete set of checked zone ids —
   * the id set is authoritative because the candidate table is paginated, so a
   * checked zone on a page the operator never re-surfaced comes back in
   * `includedIds` without a `rows` entry. Its earlier context is carried over.
   *
   * The selected row is re-pointed from the NEXT context (not `visibleTree`,
   * which lags a render): a changed type curve gives the leaf a new label so the
   * header fetch re-keys, a changed package gives it a new detail id so the
   * inventory fetch re-keys, and a dropped selection clears so the
   * default-selection effect picks the first remaining leaf.
   *
   * @param {UpdatedContextRow[]} rows - Resolved context for the rows the picker could see.
   * @param {string[]} includedIds - The complete set of checked zone ids.
   * @return {void}
   */
  const handleContextUpdate = useCallback(
    (rows: UpdatedContextRow[], includedIds: string[]) => {
      if (!workspace) return;
      const next = new Set(includedIds);

      // Rebuild the context map from the checked set: this session's resolved
      // rows win, an unseen-this-session zone keeps its earlier context, and a
      // workspace-native zone with neither just falls back to the tree default.
      const nextContext: Record<string, UpdatedContextRow> = {};
      next.forEach((id) => {
        const reported = rows.find((row) => row.rowId === id);
        if (reported) nextContext[id] = reported;
        else if (contextRows[id]) nextContext[id] = contextRows[id];
      });

      setIncludedZones(next);
      setContextRows(nextContext);
      // A package the picker reports replaces the tree's pick for that zone.
      const remainingTreePicks = retainTreePackagePicks(next, rows);

      // Re-point the selected leaf synchronously from the next context, so the
      // detail atoms (keyed on the leaf) refetch when the selected row's type
      // curve or inventory package changed.
      setSelectedLeaf((prev) => {
        if (!prev) return prev;
        const selectedKey = treeZoneKey(prev.typeCurveArea, prev.formation);
        if (!next.has(selectedKey)) return null;
        const nextTcPicks: Record<string, TreeTypeCurvePick> =
          Object.fromEntries(
            Object.values(nextContext).map((row) => [row.rowId, row.typeCurve])
          );
        const nextPkgPicks: Record<string, number> = {};
        Object.values(nextContext).forEach((row) => {
          const packageId = row.package?.inventoryFormationPackageId;
          if (packageId !== null && packageId !== undefined) {
            nextPkgPicks[row.rowId] = packageId;
          }
        });
        Object.assign(nextPkgPicks, remainingTreePicks);
        const nextTree = applyTypeCurvePicks(
          applyPackagePicks(
            selectTypeCurvePerFormation(
              filterTreeByZones(
                mergeContextZones(
                  workspace.typeCurveTree,
                  Object.values(nextContext)
                ),
                next
              )
            ),
            nextPkgPicks
          ),
          nextTcPicks
        );
        return (
          resolveTreeLeaves(nextTree).find(
            (leaf) =>
              treeZoneKey(leaf.typeCurveArea, leaf.formation) === selectedKey
          ) ?? null
        );
      });
    },
    [workspace, contextRows, retainTreePackagePicks]
  );

  const defaultPackageIds = useMemo(
    () => defaultTreePackageIds(workspace?.typeCurveTree),
    [workspace]
  );

  // Every pair the workspace was created with — the picker's pool.
  const workspaceZoneIds = useMemo(
    () => (workspace ? resolveTreeZoneKeys(workspace.typeCurveTree) : []),
    [workspace]
  );

  // The workspace's own packages per zone — the edit picker's inventory
  // dropdown source. These carry `inventoryFormationPackageId`, so a pick can
  // re-key the selected leaf; the launch candidate list can't (same name at a
  // different spacing, no id).
  const workspacePackagesByZone = useMemo<
    Record<string, TreeZonePackage[]>
  >(() => {
    if (!workspace) return {};
    const byZone: Record<string, TreeZonePackage[]> = {};
    resolveTreePackages(workspace.typeCurveTree).forEach((pkg) => {
      const zoneId = treeZoneKey(pkg.typeCurveArea, pkg.formation);
      (byZone[zoneId] ??= []).push(pkg);
    });
    return byZone;
  }, [workspace]);

  // What the left nav shows now — the picker opens on this, so edit mode
  // reflects the workspace rather than a new workspace's defaults.
  const treeSelections = useMemo(
    () => (visibleTree ? resolveTreeSelections(visibleTree) : null),
    [visibleTree]
  );

  // The launched area, so the preview map's legend leads with it.
  const originAreaName = useMemo(
    () =>
      workspace?.typeCurveTree.areas.find((area) => area.isOrigin)
        ?.typeCurveArea ?? null,
    [workspace]
  );

  // The map's all-areas layer + highlight legend: the launched area and its
  // co-developed neighbours with inline GeoJSON, straight from the show payload
  // (`map.areas`, target first). No separate geometry fetch.
  const inventoryContext = useMemo(
    () => ({
      areas: workspace?.map?.areas ?? [],
      bbox: workspace?.map?.bbox,
    }),
    [workspace]
  );

  // ─── Target (origin) unit set → map intersection filter ───
  // ─── Selected leaf's area → map area highlight ───
  // The selected type curve's area lifts out of the context set on the map
  // (higher opacity + bold border); all other areas stay dimmed.
  const setInventoryHighlightedArea = useSetAtom(
    tcInventoryHighlightedAreaAtom
  );
  useEffect(() => {
    setInventoryHighlightedArea(selectedLeaf?.typeCurveArea ?? null);
    return () => setInventoryHighlightedArea(null);
  }, [selectedLeaf?.typeCurveArea, setInventoryHighlightedArea]);

  // ─── Selected leaf's formation → gun barrel highlight ───
  const setHighlightedFormation = useSetAtom(tcHighlightedFormationAtom);
  useEffect(() => {
    setHighlightedFormation(selectedLeaf?.formation ?? null);
    return () => setHighlightedFormation(null);
  }, [selectedLeaf?.formation, setHighlightedFormation]);

  // Changing formation or package clears a compute block: either is a fresh
  // zone where Compute Inventory can run again. (A type curve change within
  // the same zone doesn't.)
  useEffect(() => {
    setComputeBlocked(false);
  }, [selectedLeaf?.formation, selectedLeaf?.inventoryFormationPackageId]);

  // ─── Selected type curve area's formations → Well-Filter / Color-by options ──
  // Only the formations falling in the selected area (with their backend swatch
  // colours) feed the shared Formation picklist and the Color-by default colour.
  // Derived from `visibleTree`, so it matches the sidebar exactly.
  const areaFormations = useMemo(() => {
    const area = visibleTree?.areas.find(
      (a) => a.typeCurveArea === selectedLeaf?.typeCurveArea
    );
    if (!area) return null;
    return area.formations.map((formation) => ({
      formation: treeFormationName(formation),
      color: formationSwatchColor(formation),
    }));
  }, [visibleTree, selectedLeaf?.typeCurveArea]);

  const setAreaFormations = useSetAtom(tcInventoryAreaFormationsAtom);
  useEffect(() => {
    setAreaFormations(areaFormations);
    return () => setAreaFormations(null);
  }, [areaFormations, setAreaFormations]);

  // ─── Reconcile Color-by when the type curve area changes ───
  // Keep colour assignments for formations that also exist in the newly-selected
  // area; drop the rest. New-area formations are not auto-added — the operator
  // adds them via the Color-by modal. No-ops when nothing needs pruning.
  const setColorByRules = useSetAtom(tcColorByRulesAtom);
  useEffect(() => {
    if (!areaFormations) return;
    const allowed = new Set(areaFormations.map((f) => f.formation));
    setColorByRules((prev) => {
      let changed = false;
      const next = prev.map((rule: ColorByConfig) => {
        if (rule.attribute !== 'formation') return rule;
        const keptValues = rule.selectedValues.filter((v) => allowed.has(v));
        if (keptValues.length === rule.selectedValues.length) return rule;
        changed = true;
        const keptColors: Record<string, string> = {};
        for (const v of keptValues) {
          const c = rule.valueColors[v];
          if (c !== undefined) keptColors[v] = c;
        }
        return { ...rule, selectedValues: keptValues, valueColors: keptColors };
      });
      return changed ? next : prev;
    });
  }, [areaFormations, setColorByRules]);

  // ─── Selected leaf's formation → Wells section Formation filter ───
  // Clicking a type curve in the sidebar filters the Wells table to that curve's
  // formation. One-shot per formation change (the operator can still adjust it
  // between selections); the prior value is restored on unmount so plain Type
  // Curve pages are untouched.
  const setWellFilters = useSetAtom(tcWellFiltersAtom);
  const priorFormationRef = useRef<TcWellFilters['formation']>(undefined);
  const formationCapturedRef = useRef(false);
  useEffect(() => {
    const formation = selectedLeaf?.formation;
    if (!formation) return;
    setWellFilters((prev) => {
      // Capture the pre-inventory value once, from the same `prev` React passes
      // to both StrictMode invocations, so it is captured deterministically.
      if (!formationCapturedRef.current) {
        priorFormationRef.current = prev.formation;
        formationCapturedRef.current = true;
      }
      return { ...prev, formation: [formation] };
    });
  }, [selectedLeaf?.formation, setWellFilters]);
  useEffect(
    () => () => {
      if (!formationCapturedRef.current) return;
      setWellFilters((prev) => {
        const next = { ...prev };
        if (priorFormationRef.current === undefined) delete next.formation;
        else next.formation = priorFormationRef.current;
        return next;
      });
    },
    [setWellFilters]
  );

  // ─── Gun barrel: scope to the inventory backend (inventory_unit_slots layer) ───
  // The selected/default package id of every formation the workspace covers,
  // across ALL areas (not just the origin) — one id per formation. The gun barrel
  // and slot table scope to this whole set (they draw the full stratigraphic
  // column); the map slot layer uses only the single selected id above.
  const workspacePackageIds = useMemo(
    () =>
      Array.from(
        new Set(
          (visibleTree ? resolveTreeLeaves(visibleTree) : [])
            .map((leaf) => leaf.inventoryFormationPackageId)
            .filter((packageId): packageId is number => packageId !== null)
        )
      ),
    [visibleTree]
  );
  const setGunBarrelScope = useSetAtom(tcInventoryGunBarrelScopeAtom);
  useEffect(() => {
    setGunBarrelScope(
      id
        ? {
            inventoryWorkspaceId: Number(id),
            inventoryFormationPackageId:
              selectedLeaf?.inventoryFormationPackageId ?? null,
            inventoryFormationPackageIds: workspacePackageIds,
          }
        : null
    );
    return () => setGunBarrelScope(null);
  }, [
    id,
    selectedLeaf?.inventoryFormationPackageId,
    workspacePackageIds,
    setGunBarrelScope,
  ]);

  // ─── Selected zone's read-only spacing → gun barrel controls ───
  // The gun barrel shows these in place of the editable Target Spacing input on
  // the inventory page; null-on-unmount keeps the plain Type Curve page's input.
  const setInventorySpacing = useSetAtom(tcInventorySpacingAtom);
  useEffect(() => {
    setInventorySpacing(
      inventoryTypeCurve
        ? {
            wellSpacingFt: inventoryTypeCurve.wellSpacingFt,
            crossLineSetbackFt: inventoryTypeCurve.crossLineSetbackFt,
            inLineSetbackFt: inventoryTypeCurve.inLineSetbackFt,
            spacingToleranceFt: inventoryTypeCurve.spacingToleranceFt,
          }
        : null
    );
    return () => setInventorySpacing(null);
  }, [inventoryTypeCurve, setInventorySpacing]);

  const leafOrder = useMemo(
    () => (visibleTree ? resolveTreeLeaves(visibleTree) : []),
    [visibleTree]
  );

  const leafIndex = selectedLeaf
    ? leafOrder.findIndex((leaf) => leaf.key === selectedLeaf.key)
    : -1;

  const stepFormation = useCallback(
    (delta: 1 | -1) => {
      if (leafIndex < 0) return;
      const next = leafOrder[leafIndex + delta];
      if (next) setSelectedLeaf(next);
    },
    [leafIndex, leafOrder]
  );

  // The selected Type Curve's header powers the details content. The route
  // resolves by tc_public_id, never the numeric detail id and never the leaf's
  // label — a renamed workspace makes the label a display name that resolves to
  // nothing. A null tc_public_id (or detail id) means the family has no promoted
  // header to fetch, which renders the empty state rather than an error.
  const headerHref =
    selectedLeaf &&
    selectedLeaf.typeCurveHeaderDetailsId !== null &&
    selectedLeaf.tcPublicId
      ? `/type_curve_headers/${encodeURIComponent(selectedLeaf.tcPublicId)}`
      : undefined;
  const headerAtom = useMemo(
    () => createResourceAtom<TypeCurveHeader>(headerHref),
    [headerHref]
  );
  const [headerResource, refreshHeader] = useAtom(headerAtom);
  const { data: headerData } = formatResource(headerResource);

  // Empty-state flag the sidebar and data calls key on, mirroring the Type
  // Curve pages. `null` while the header is still loading; a header-less leaf
  // is an explicit empty state.
  useEffect(() => {
    setHasTypeCurveArea(
      headerData
        ? headerData.typeCurveAreaId !== null
        : selectedLeaf && !headerHref
          ? false
          : null
    );
    return () => setHasTypeCurveArea(null);
  }, [headerData, headerHref, selectedLeaf, setHasTypeCurveArea]);

  // ─── Header development type → Wells section Hole Direction filter ───
  // The wells query already sends the header's development type as a hole-direction
  // default; seed it as an APPLIED filter so it shows as a (removable) chip rather
  // than a hidden filter. Keyed on `developmentType` so it lands once the header
  // resolves (the shared hook seed one-shots at mount, before it loads). Seeds only
  // when the key is absent, so a user who cleared the chip (present-but-empty []) is
  // respected. Prior value restored on unmount so plain Type Curve pages are untouched.
  const priorHoleDirectionRef =
    useRef<TcWellFilters['holeDirection']>(undefined);
  const holeDirectionCapturedRef = useRef(false);
  useEffect(() => {
    const dev = headerData?.developmentType;
    if (!dev) return;
    setWellFilters((prev) => {
      if (!holeDirectionCapturedRef.current) {
        priorHoleDirectionRef.current = prev.holeDirection;
        holeDirectionCapturedRef.current = true;
      }
      if ('holeDirection' in prev) return prev;
      return { ...prev, holeDirection: [dev.toUpperCase()] };
    });
  }, [headerData?.developmentType, setWellFilters]);
  useEffect(
    () => () => {
      if (!holeDirectionCapturedRef.current) return;
      setWellFilters((prev) => {
        const next = { ...prev };
        if (priorHoleDirectionRef.current === undefined)
          delete next.holeDirection;
        else next.holeDirection = priorHoleDirectionRef.current;
        return next;
      });
    },
    [setWellFilters]
  );

  // Own the MAIN surface's inventory `/gun_barrel` fetch here (page-level, always
  // mounted), writing `gunBarrelStateAtoms.main`. The Unit Builder owns its own
  // 'builder' fetch inside the modal; the request is formation-agnostic (each
  // surface highlights its own formation client-side).
  useInventoryGunBarrelFor(
    'main',
    headerData?.id ?? null,
    headerData?.structureGridCollectionId ?? null
  );

  // Own the MAIN surface's inventory slot-table fetch here (page-level, always
  // mounted), writing `slotTableStateAtoms.main`. The Unit Builder owns its own
  // 'builder' fetch inside the modal; the two never share a copy.
  useUnitFormationSlotsFor(
    'main',
    headerData?.id ?? null,
    headerData?.structureGridCollectionId ?? null
  );

  // The builder's live spacing edits are per-unit and reset when the selection
  // changes or an LTD generation restamps the configs (the fresh config becomes
  // the shown value). Cleared here at the page so it happens even while the
  // builder is open. (The main surface's copy is never written.)
  const selectedUnitId = useAtomValue(tcSelectedDevAreaAtom)?.developmentAreaId;
  const ltdRefreshTrigger = useAtomValue(ltdRefreshTriggerAtom);
  const setSpacingEdits = useSetAtom(spacingEditsAtoms.builder);
  // The builder's edited section line is also per-unit — clear it on unit change
  // / LTD regen so the builder falls back to the new unit's own cross-section.
  const setBuilderSectionCross = useSetAtom(builderSectionCrossAtom);
  useEffect(() => {
    setSpacingEdits({});
    setBuilderSectionCross(null);
  }, [
    selectedUnitId,
    ltdRefreshTrigger,
    setSpacingEdits,
    setBuilderSectionCross,
  ]);

  if (!id) {
    return (
      <InlineNotification
        kind="error"
        title="Missing workspace id"
        lowContrast
      />
    );
  }

  if (showResource.loading && !workspace) {
    return <Loading />;
  }

  if (showError || !workspace) {
    return (
      <InlineNotification
        kind="error"
        title={showError ?? 'Something went wrong'}
        lowContrast
      />
    );
  }

  // Unpromoted workspaces edit the spacing values (mock's edit state);
  // promoted ones read the stored inventory.
  const inventoryEditable = !workspace.promoted;

  const renderBody = () => {
    if (!selectedLeaf) {
      return (
        <InlineNotification
          kind="info"
          title="This workspace has no type curves."
          lowContrast
        />
      );
    }
    if (!headerHref) {
      // No promoted header for this family — only the inventory details are
      // renderable; every other section hangs off the header.
      return (
        <>
          <InlineNotification
            kind="info"
            title="This Type Curve has no promoted header yet."
            lowContrast
          />
          {inventoryTypeCurve ? (
            <InventoryDetailsSection
              data={inventoryTypeCurve}
              editable={inventoryEditable}
              computing={computingInventory || computeRunInProgress}
              computeBlocked={computeBlocked}
              onCompute={handleComputeInventory}
            />
          ) : null}
        </>
      );
    }
    if (headerResource.loading && !headerData) {
      return <Loading withOverlay={false} />;
    }
    if (!headerData) {
      return (
        <InlineNotification
          kind="error"
          title="The Type Curve could not be loaded"
          lowContrast
        />
      );
    }
    return (
      <TypeCurveDetailsContent
        data={headerData}
        // The content forwards this to TypeCurveMap, which resolves the
        // Type Curve by it — so it must be the selected curve's public id,
        // not this workspace's own id.
        id={headerData.tcPublicId ?? id}
        inventoryTypeCurve={inventoryTypeCurve ?? null}
        inventoryEditable={inventoryEditable}
        computingInventory={computingInventory || computeRunInProgress}
        computeInventoryBlocked={computeBlocked}
        onComputeInventory={handleComputeInventory}
        onEditSlots={(unit) => {
          // The button always opens on the current selection (`unit` is derived
          // from it), so the scoped unit follows from `tcSelectedDevAreaAtom` — no
          // unit id to carry here. With nothing selected it opens an empty draft;
          // clear stale survey rows so that panel starts blank.
          if (!unit) setSurveyRows([]);
          // Seed the builder's gun barrel + slot table from the main page's
          // live state BEFORE mounting the modal, so the builder shows it
          // instantly and its fetch hooks skip the redundant request.
          seedBuilderFromMain();
          setBuilderOpen(true);
        }}
        unitBuilderOpen={builderOpen}
        sections={INVENTORY_PAGE_SECTIONS}
        onRefreshHeader={refreshHeader}
        inventoryContext={inventoryContext}
      />
    );
  };

  return (
    <div className={style.pageWrapper}>
      <Helmet>
        <title>{`${workspace.name} — Inventory Workspace`}</title>
      </Helmet>
      {/* Activity feed + comment thread — the shared Type Curve events panel,
          opened from the header button via isEventsPanelOpenAtom. Links come off
          the workspace payload's resources. */}
      {workspace.resources?.events?.href &&
        workspace.resources?.comments?.href && (
          <EventsPanel
            header={workspace.name}
            title="Inventory Workspace"
            eventsHref={workspace.resources.events.href}
            commentsHref={workspace.resources.comments.href}
          />
        )}
      <InventoryWorkspaceHeader
        workspace={workspace}
        typeCurveLabel={selectedLeaf?.label ?? null}
        leftPanelOpen={leftOpen}
        onLeftPanelToggle={setLeftOpen}
        tasksPanelOpen={rightPanel.type === 'workspace-tasks'}
        taskCompleted={checklistCompleted}
        taskTotal={checklistTasks.length}
        gateStatus={gateTask?.status ?? null}
        gateSaving={saveGateTask.isPending}
        // Clicking an inactive verdict opens the decision panel to capture a
        // reason; re-clicking the active verdict reverts directly (no reason).
        onMarkCorrect={() => {
          if (gateTask?.status === 'correct') {
            saveGateTask.mutate({
              path: GATE_TASK_PATH,
              correctnessState: null,
              reason: '',
            });
          } else {
            setRightPanel({ type: 'verification', kind: 'correct' });
          }
        }}
        onMarkIncorrect={() => {
          if (gateTask?.status === 'incorrect') {
            saveGateTask.mutate({
              path: GATE_TASK_PATH,
              correctnessState: null,
              reason: '',
            });
          } else {
            setRightPanel({ type: 'verification', kind: 'incorrect' });
          }
        }}
        canPromote={canPromote}
        onPromote={() => setPromoteModalOpen(true)}
        onToggleTasks={() =>
          setRightPanel((prev) => {
            return prev.type === 'workspace-tasks'
              ? { type: 'none' }
              : { type: 'workspace-tasks' };
          })
        }
        onBack={() => {
          if (history.length > 1) {
            history.goBack();
          } else {
            history.push(routes.typeCurvesPath);
          }
        }}
      />
      {/* Progress of a rotate's background regeneration, mirrored in the builder
          modal — the operator sees it whether the builder is open or not. */}
      <UnitRegenerationBanner />
      <DualSidebarLayout
        left={{
          content: (
            <>
              <InventoryTypeCurveTree
                tree={visibleTree ?? workspace.typeCurveTree}
                // Hidden on a promoted workspace, which is read-only.
                onEditCurves={
                  workspace.promoted ? undefined : () => setEditCurvesOpen(true)
                }
                selectedKey={selectedLeaf?.key ?? null}
                onSelect={setSelectedLeaf}
                onSelectPackage={selectPackage}
                defaultPackageIds={defaultPackageIds}
              />
              <TypeCurvePageFilters
                wellCount={wellsTotalCount || undefined}
                unitCount={unitsTotalCount || undefined}
                onEditColorRule={setEditingColorRuleId}
                onEditUnitColorRule={setEditingUnitColorRuleId}
                dcaScenarioName={selectedDcaScenario?.name}
                onEditDcaScenario={() => setRightPanel({ type: 'dca' })}
                typeCurveAreaName={headerData?.typeCurveAreaName}
                sections={INVENTORY_PAGE_SECTIONS}
              />
            </>
          ),
          isOpen: leftOpen,
        }}
        right={{
          content: (
            <>
              {rightPanel.type === 'dca' ? (
                <DcaScenarioSelection
                  onClose={() => setRightPanel({ type: 'none' })}
                />
              ) : null}
              {rightPanel.type === 'workspace-tasks' && workspaceIdNum ? (
                <WorkspaceTasksPanel
                  workspaceId={workspaceIdNum}
                  tasks={checklistTasks}
                  completed={checklistCompleted}
                  total={checklistTasks.length}
                  loading={tasksQuery.isPending}
                  promoted={workspace.promoted}
                  onClose={() => setRightPanel({ type: 'none' })}
                />
              ) : null}
              {rightPanel.type === 'verification' && workspaceIdNum ? (
                <InventoryVerificationPanel
                  workspaceId={workspaceIdNum}
                  kind={rightPanel.kind}
                  onClose={() => setRightPanel({ type: 'none' })}
                />
              ) : null}
            </>
          ),
          isOpen: rightPanel.type !== 'none',
        }}
      >
        {renderBody()}
      </DualSidebarLayout>
      <TcWellFilterModal />
      <TcUnitFilterModal />
      <TcColorByModal
        editingRuleId={editingColorRuleId}
        onClose={() => setEditingColorRuleId(null)}
        typeCurveHeader={headerData ?? null}
      />
      <TcUnitColorByModal
        editingRuleId={editingUnitColorRuleId}
        onClose={() => setEditingUnitColorRuleId(null)}
        typeCurveHeader={headerData ?? null}
      />
      <TcCrossPlotChartModal />
      {/* Mounted only while open: Carbon keeps closed modals in the DOM, and a
          fresh mount reseeds the dummy picks each time. */}
      {builderOpen && (
        <BuilderSurfaceFetchers
          typeCurveId={headerData?.id ?? null}
          structureGridCollectionId={
            headerData?.structureGridCollectionId ?? null
          }
        />
      )}
      {builderOpen && (
        <UnitBuilderModal
          open
          inventoryWorkspaceId={Number(id)}
          promoted={workspace.promoted}
          unitId={scopedUnit.unitId}
          unitName={scopedUnit.unitName}
          typeCurveHeaderDetailsId={
            selectedLeaf?.typeCurveHeaderDetailsId ?? null
          }
          onClose={() => {
            setBuilderOpen(false);
            resetBuilderSurface();
          }}
          pager={<TcUnitNavigator compact />}
          mapPane={
            // Feed the map the type curve's STRING public id, never the numeric
            // workspace `id` — `/type_curve_headers/{workspaceId}` 404s. Undefined
            // until `headerData` resolves makes the header fetch a no-op (mirrors
            // `headerHref` above); the map only reads `data`.
            <UnitBuilderMap typeCurveId={headerData?.tcPublicId} />
          }
          gunBarrelPane={
            headerData ? (
              <UnitBuilderGunBarrel
                typeCurveId={headerData.id}
                typeCurveHeaderDetailsId={headerData.id}
                formationCode={
                  inventoryTypeCurve
                    ? detailsFormationName(inventoryTypeCurve)
                    : null
                }
                inventoryFormationPackageId={
                  selectedLeaf?.inventoryFormationPackageId ?? null
                }
                onStepFormation={stepFormation}
                canStepFormationUp={leafIndex > 0}
                canStepFormationDown={
                  leafIndex >= 0 && leafIndex < leafOrder.length - 1
                }
                storedSpacing={
                  inventoryTypeCurve
                    ? {
                        wellSpacingFt: inventoryTypeCurve.wellSpacingFt,
                        crossLineSetbackFt:
                          inventoryTypeCurve.crossLineSetbackFt,
                        inLineSetbackFt: inventoryTypeCurve.inLineSetbackFt,
                        spacingToleranceFt:
                          inventoryTypeCurve.spacingToleranceFt,
                      }
                    : undefined
                }
                structureGridCollectionId={
                  headerData.structureGridCollectionId ?? null
                }
                horizontalWellSpacingFt={
                  headerData.horizontalWellSpacingFt ?? 200
                }
              />
            ) : undefined
          }
        />
      )}

      {promoteModalOpen && (
        <PromoteWorkspaceModal
          open
          workspace={workspace}
          typeCurveLabel={selectedLeaf?.label ?? null}
          // Same checklist the header badge and tasks panel read, so a verdict
          // saved there shows here rather than the modal going stale.
          gateTask={gateTask}
          tasks={checklistTasks}
          onClose={() => setPromoteModalOpen(false)}
          // Promotion flips `promoted`, which drives the header and switches
          // every editable section (spacing, Compute Inventory) to read-only.
          onPromoted={() => void refreshShow()}
        />
      )}

      {editCurvesOpen && (
        <CreateInventoryWorkspaceModal
          open
          mode="edit-context"
          // The picker shows every candidate the backend returns; the ticks are
          // the zones the left-nav shows right now (all others are add candidates).
          initialIncluded={[...(includedZones ?? workspaceZoneIds)]}
          initialTypeCurves={treeSelections?.typeCurves}
          initialPackages={treeSelections?.packages}
          workspacePackagesByZone={workspacePackagesByZone}
          typeCurveHeaderDetailsId={selectedLeaf?.typeCurveHeaderDetailsId}
          uniqueTcId={workspace.uniqueTcId}
          typeCurveAreaName={originAreaName}
          onUpdateContext={handleContextUpdate}
          onClose={() => setEditCurvesOpen(false)}
        />
      )}
    </div>
  );
};
